import React from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Card } from 'antd';
import { motion } from 'framer-motion';
import { PageFade } from '../components/PageTransition';
import { aboutPage } from '../data/hotelData';
import { Helmet } from 'react-helmet';

const { Title, Paragraph } = Typography;

// --- Styled Components ---

const HeroSection = styled.div`
  height: 70vh;
  background: url(${aboutPage.banner}) center/cover no-repeat fixed; /* Fixed = Parallax effect */
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.5); /* Dark overlay */
  }

  h1 {
    position: relative;
    z-index: 2;
    color: white;
    font-size: 5rem;
    font-family: 'Playfair Display', serif;
    letter-spacing: 5px;
    text-transform: uppercase;
    text-align: center;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
`;

const IntroSection = styled.div`
  padding: 6rem 10%;
  text-align: center;
  background: #fff;
`;

const StatsBar = styled.div`
  background: #1a1a1a;
  color: #d4af37;
  padding: 4rem 10%;
`;

const StatItem = styled.div`
  text-align: center;
  
  .value {
    font-size: 3.5rem;
    font-family: 'Playfair Display', serif;
    font-weight: 700;
  }
  .label {
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #fff;
    margin-top: 0.5rem;
  }
`;

const TilesSection = styled.div`
  padding: 4rem 5%;
  background: #f4f4f4;
`;

const TileCard = styled(motion.div)`
  height: 400px;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  background-image: url(${props => props.$bg});
  background-size: cover;
  background-position: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  
  /* If it's a text tile */
  background-color: ${props => props.$dark ? '#1a1a1a' : '#fff'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 3rem;
  
  transition: transform 0.4s ease;

  &:hover {
    transform: translateY(-10px);
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: 2.5rem;
    color: ${props => props.$dark ? '#d4af37' : '#1a1a1a'};
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: ${props => props.$dark ? '#ccc' : '#555'};
  }
`;

// --- Animations ---
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

// --- Component ---

const About = () => {
  return (
    <PageFade>
      <Helmet>
        <title>Our Legacy | Miran Suites</title>
        <meta name="description" content="Discover the heritage of Miran Suites. A sanctuary of elegance redefining hospitality." />
      </Helmet>

      {/* Hero */}
      <HeroSection>
        <motion.h1 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          The Miran Legacy
        </motion.h1>
      </HeroSection>

      {/* Intro Text */}
      <IntroSection>
        <Row justify="center">
          <Col xs={24} md={16}>
            <Title level={2} style={{ fontFamily: 'Playfair Display', marginBottom: '2rem' }}>
              Redefining Urban Luxury
            </Title>
            <Paragraph style={{ fontSize: '1.2rem', lineHeight: '2', color: '#666' }}>
              Established with a vision to create a sanctuary in the city, Miran Suites is more than just a hotel; 
              it is an experience curated for the discerning traveler. Our philosophy is rooted in 
              the belief that true luxury lies in the details—from the thread count of our linens 
              to the warmth of our welcome.
            </Paragraph>
            <div style={{ width: '60px', height: '2px', background: '#d4af37', margin: '3rem auto 0' }}></div>
          </Col>
        </Row>
      </IntroSection>

      {/* Stats Bar */}
      <StatsBar>
        <Row gutter={[32, 32]}>
          {aboutPage.stats.map((stat, index) => (
            <Col xs={12} md={6} key={index}>
              <StatItem>
                <div className="value">{stat.value}</div>
                <div className="label">{stat.label}</div>
              </StatItem>
            </Col>
          ))}
        </Row>
      </StatsBar>

      {/* Modern Tiles Grid */}
      <TilesSection>
        <Row gutter={[24, 24]}>
          {aboutPage.tiles.map((tile, index) => (
            <Col xs={24} md={12} lg={12} key={tile.id}>
              {/* Conditional Rendering: Alternating Layouts */}
              <TileCard
                $bg={index % 2 === 0 ? tile.image : ''} // Even tiles are images
                $dark={tile.dark}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
              >
                {/* If it's NOT an image tile, show text */}
                {index % 2 !== 0 && (
                  <>
                    <h3>{tile.title}</h3>
                    <p>{tile.text}</p>
                  </>
                )}
                
                {/* Overlay text on Image tiles for extra style (optional) */}
                {index % 2 === 0 && (
                  <div style={{ 
                    position: 'absolute', bottom: 20, left: 20, 
                    background: 'white', padding: '10px 20px', 
                    fontWeight: 'bold', letterSpacing: '1px' 
                  }}>
                    {tile.title.toUpperCase()}
                  </div>
                )}
              </TileCard>
            </Col>
          ))}
          
          {/* Second Row Inverse Logic to create checkerboard effect */}
          {aboutPage.tiles.map((tile, index) => (
             /* This is a simple loop, for a complex checkerboard you might manually arrange Cols 
                or map a more complex data structure. 
                For now, the map above creates a nice Image | Text | Image | Text flow if data is ordered correctly. 
             */
             null
          ))}
        </Row>
      </TilesSection>

    </PageFade>
  );
};

export default About;